import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { visionService } from "@/services/vision-service";
import { reportError } from "@/utils/observability";
import type { ExtractionStatus, ImportDealResponse } from "../types";

type ImportStatus = "idle" | "loading" | "success" | "error";

export type ImportErrorKind = "not_found" | "server";

const BRIEF_POLL_INTERVAL_MS = 2500;
// Backend marks a pending extraction as failed after ~3min of staleness;
// this is only a client-side backstop against a poll that never resolves.
const BRIEF_POLL_TIMEOUT_MS = 4 * 60_000;

export interface UseImportDealReturn {
  status: ImportStatus;
  errorKind: ImportErrorKind | null;
  data: ImportDealResponse | null;
  /**
   * Lifecycle of the backend's background AI extraction. Null before any
   * import. "pending" while the LLM runs (the form is already prefilled
   * with deterministic CRM data); "ready" when fullBrief is available.
   */
  extractionStatus: ExtractionStatus | null;
  /** Complete GoldenBrief — null until extractionStatus is "ready". */
  fullBrief: ImportDealResponse | null;
  importDeal: (
    dealId: string,
    options?: { forceRefresh?: boolean },
  ) => Promise<ImportDealResponse | null>;
  reset: () => void;
}

function resolveErrorKind(error: unknown): ImportErrorKind {
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return "not_found";
  }
  return "server";
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Estado compartilhado do import: quando o AIModeWizard provê o context, o
 * polling da extração sobrevive à troca de steps (o DiagnosisStep desmonta,
 * mas o estado vive no wizard) e alimenta a conciliação + o gate do persist.
 */
export const ImportDealContext = createContext<UseImportDealReturn | null>(
  null,
);

/**
 * Consome o estado do provider quando existe; fora do wizard (testes,
 * usos standalone) cai no estado local — mesmo comportamento de antes,
 * incluindo o cancelamento do polling no unmount.
 */
export function useImportDeal(): UseImportDealReturn {
  const shared = useContext(ImportDealContext);
  const local = useImportDealState();
  return shared ?? local;
}

export function useImportDealState(): UseImportDealReturn {
  const [status, setStatus] = useState<ImportStatus>("idle");
  const [errorKind, setErrorKind] = useState<ImportErrorKind | null>(null);
  const [data, setData] = useState<ImportDealResponse | null>(null);
  const [extractionStatus, setExtractionStatus] =
    useState<ExtractionStatus | null>(null);
  const [fullBrief, setFullBrief] = useState<ImportDealResponse | null>(null);
  // Cancellation token of the active poll loop — a new import, a reset or an
  // unmount flips `cancelled` so the stale loop stops touching state.
  const pollTokenRef = useRef<{ cancelled: boolean } | null>(null);

  function stopPolling() {
    if (pollTokenRef.current) pollTokenRef.current.cancelled = true;
  }

  useEffect(() => {
    return () => stopPolling();
  }, []);

  // Finite post-mutation status poll (not view data — SWR's cache/revalidate
  // semantics don't fit a one-shot pending→ready transition).
  async function pollBrief(dealId: string): Promise<void> {
    stopPolling();
    const token = { cancelled: false };
    pollTokenRef.current = token;
    const startedAt = Date.now();

    while (!token.cancelled) {
      await sleep(BRIEF_POLL_INTERVAL_MS);
      if (token.cancelled) return;
      try {
        const result = await visionService.getImportDealBrief(dealId);
        if (token.cancelled) return;
        if (result.status === "pending") {
          if (Date.now() - startedAt >= BRIEF_POLL_TIMEOUT_MS) {
            setExtractionStatus("failed");
            return;
          }
          continue;
        }
        if (result.status === "ready") {
          if (!result.brief) {
            // "ready" sem brief é um estado inconsistente do backend —
            // reportar ready aqui desbloquearia o wizard sem nunca hidratar.
            setExtractionStatus("failed");
            return;
          }
          const brief = {
            ...result.brief,
            extraction_status: "ready" as const,
          };
          setFullBrief(brief);
          setData(brief);
        }
        setExtractionStatus(result.status);
        return;
      } catch (error) {
        if (token.cancelled) return;
        // Sem resposta HTTP (timeout do request, queda de rede, pod frio
        // demorando) = transiente: continua o polling até o backstop.
        // Com resposta (404 = expirou/nunca importado, 4xx/5xx) = terminal —
        // o retry do usuário é reimportar o deal.
        const isTransient = axios.isAxiosError(error) && !error.response;
        if (isTransient && Date.now() - startedAt < BRIEF_POLL_TIMEOUT_MS) {
          continue;
        }
        reportError(error, {
          operation: "getImportDealBrief",
          step: "diagnosis",
          dealId,
        });
        setExtractionStatus("failed");
        return;
      }
    }
  }

  async function importDeal(
    dealId: string,
    options?: { forceRefresh?: boolean },
  ): Promise<ImportDealResponse | null> {
    setStatus("loading");
    setErrorKind(null);
    stopPolling();
    setExtractionStatus(null);
    setFullBrief(null);
    try {
      const result = await visionService.importDeal(dealId, options);
      setData(result);
      setStatus("success");
      // Backends antigos (extração síncrona) não enviam o campo → ready.
      const extraction = result.extraction_status ?? "ready";
      setExtractionStatus(extraction);
      if (extraction === "pending") {
        void pollBrief(dealId);
      } else if (extraction === "ready") {
        setFullBrief(result);
      }
      return result;
    } catch (error) {
      reportError(error, {
        operation: "importDeal",
        step: "diagnosis",
        dealId,
      });
      setErrorKind(resolveErrorKind(error));
      setStatus("error");
      return null;
    }
  }

  function reset() {
    stopPolling();
    setStatus("idle");
    setErrorKind(null);
    setData(null);
    setExtractionStatus(null);
    setFullBrief(null);
  }

  return {
    status,
    errorKind,
    data,
    extractionStatus,
    fullBrief,
    importDeal,
    reset,
  };
}
